import { RefObject, useCallback, useEffect, useState } from 'react';
import { arrow, flip,FloatingArrow, useFloating } from '@floating-ui/react';
import clsx from 'clsx';

import styles from './tooltip.module.scss';

export type TooltipEllipsisProps = {
  /** parent reference containing truncated child elements  */
  container: RefObject<HTMLElement>;
};

/**
 * Add tooltip support to any contained truncated text-element
 */
export function TooltipEllipsis({ container }: TooltipEllipsisProps) {
  const [arrowElement, setArrowElement] = useState<SVGElement | null>(null);
  const { refs, elements, floatingStyles, context } =
    useFloating<HTMLDivElement>({
      placement: 'bottom-start',
      middleware: [
        flip(),
        arrow({
          element: arrowElement,
        }),
      ],
    });

  const hide = useCallback(() => refs.setReference(null), [refs]);

  useEffect(() => {
    function onMove(event: MouseEvent) {
      const hoveredElement = event.target as HTMLElement;

      if (hoveredElement == null) {
        hide();
        return;
      }
      // is this a table cell?
      if (hoveredElement.firstChild?.nodeName !== '#text') {
        hide();
        return;
      }

      // do not show tooltip is hovered element is empty
      // (also ensures it is not active on ag-grid scrollbar)
      const textContent = hoveredElement.firstChild.textContent ?? '';
      if (!textContent.trim()) {
        hide();
        return;
      }

      let overflowElement = hoveredElement;

      // <match> is used for text highlighting, use parent el in that case
      if (
        hoveredElement.tagName === 'MATCH' &&
        hoveredElement.parentElement != null
      ) {
        overflowElement = hoveredElement.parentElement;
      }

      // do we have an overflow?
      if (overflowElement.offsetWidth >= overflowElement.scrollWidth) {
        hide();
        return;
      }

      refs.setReference(overflowElement);
    }
    const parent = container.current;
    if (parent) {
      parent.addEventListener('mousemove', onMove);
      parent.addEventListener('mouseleave', hide);
      return () => {
        parent.removeEventListener('mousemove', onMove);
        parent.removeEventListener('mouseleave', hide);
      };
    }
    return undefined;
  }, [hide, container, refs]);

  const hoveredElement = elements.reference as HTMLDivElement;
  if (hoveredElement == null) {
    return;
  }

  return (
    <div
      ref={refs.setFloating}
      className={clsx(styles['root'], styles['tooltip-ellipsis'])}
      style={{
        display: elements.reference ? 'block' : 'none',
        zIndex: 102,
        padding: '0.5em',
        ...floatingStyles,
      }}
    >
      {hoveredElement.textContent}
      <FloatingArrow ref={setArrowElement} context={context} />
    </div>
  );
}
